import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import CurrencyLogo from 'components/CurrencyLogo'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { useApiTokenValue } from 'hooks/useTokenPrice'
import React, { useMemo } from 'react'
import { CheckCircle } from 'react-feather'
import styled from 'styled-components'
import { ThemedText } from 'theme'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import TransactionError from '../../TransactionError'

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 4px;
  width: 100%;
  margin: 0 auto;
`

const SectionLabel = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  margin-bottom: 12px;
`

const RewardAmount = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.cardTertiary};
  border-radius: 12px;
  margin-top: 8px;
`

const DestinationInfo = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.backgroundModule};
  border-radius: 12px;
  margin-top: 8px;
`

const WalletDetails = styled.div`
  flex: 1;
`

const WalletLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
`

const WalletAddress = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 2px;
`

const ConfirmButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  border-radius: 12px;
  border: none;
  background: ${({ disabled, theme }) => (disabled ? theme.backgroundInteractive : theme.accentAction)};
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.hoverState};
  }
`

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 0;
`

const SuccessIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: ${({ theme }) => `${theme.accentSuccess}20`};
  margin-bottom: 16px;
`

interface RewardVaultClaimStepProps extends Omit<ConfirmationTransactionStepProps, 'children' | 'onConfirm'> {
  next: () => void
  earnedAmount?: CurrencyAmount<Token>
  account?: string
  claimDestination: 'connected' | 'other'
  otherWalletAddress: string
  onClaim: () => Promise<boolean | null>
  error: string
}

export default function RewardVaultClaimStep({
  next,
  earnedAmount,
  account,
  claimDestination,
  otherWalletAddress,
  onClaim,
  attemptingTxn,
  hash,
  error,
  ...rest
}: RewardVaultClaimStepProps) {
  const { value: fiatValue } = useApiTokenValue(earnedAmount)

  // Format the amount for display
  const formattedAmount = earnedAmount ? earnedAmount.toSignificant(6) : '0'

  // Check if the transaction is complete (has hash and not attempting)
  const isClaimComplete = useMemo(() => hash && !attemptingTxn && !error, [hash, attemptingTxn, error])

  // Get destination address display
  const destinationAddress = claimDestination === 'connected' ? account || 'Connected wallet' : otherWalletAddress
  const rewardSymbol = earnedAmount?.currency.symbol
  const rewardLabel = rewardSymbol ? `${rewardSymbol} rewards` : 'rewards'
  const claimButtonText = rewardSymbol ? `Claim ${rewardSymbol} Rewards` : 'Claim Rewards'

  // Truncate address for display
  const truncateAddress = (address: string) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
  }

  // Handle claim button click
  const handleClaim = async () => {
    const success = await onClaim()
    if (success) {
      next()
    }
  }

  // If claim is complete, show success message
  if (isClaimComplete) {
    return (
      <ConfirmationTransactionStep attemptingTxn={false} hash={hash} {...rest} onConfirm={next}>
        <StepContainer>
          <SuccessContainer>
            <SuccessIconWrapper>
              <CheckCircle size={32} color="#28A745" />
            </SuccessIconWrapper>

            <ThemedText.DeprecatedLargeHeader marginBottom="8px">Claim Successful!</ThemedText.DeprecatedLargeHeader>

            <ThemedText.DeprecatedBody color="textSecondary" marginBottom="16px">
              Your {rewardLabel} have been claimed successfully.
            </ThemedText.DeprecatedBody>

            <RewardAmount>
              <CurrencyLogo currency={earnedAmount?.currency} size="24px" />
              <ThemedText.DeprecatedBody marginLeft="10px" fontWeight="600" fontSize="16px">
                {formattedAmount} {earnedAmount?.currency.symbol}
              </ThemedText.DeprecatedBody>
              <ThemedText.DeprecatedBody marginLeft="auto" color="green" fontSize="15px">
                {formatDollarAmount(fiatValue)}
              </ThemedText.DeprecatedBody>
            </RewardAmount>

            <ConfirmButton onClick={next}>Continue</ConfirmButton>
          </SuccessContainer>
        </StepContainer>
      </ConfirmationTransactionStep>
    )
  }

  // Main claim step UI
  return (
    <ConfirmationTransactionStep attemptingTxn={attemptingTxn} hash={hash} {...rest} onConfirm={next}>
      <StepContainer>
        <ThemedText.DeprecatedBody fontSize="14px" color="textSecondary">
          You're about to claim your {rewardLabel}.
        </ThemedText.DeprecatedBody>

        {/* Amount to claim */}
        <div>
          <SectionLabel>Amount to Claim</SectionLabel>
          <RewardAmount>
            <CurrencyLogo currency={earnedAmount?.currency} size="24px" />
            <ThemedText.DeprecatedBody marginLeft="10px" fontWeight="600" fontSize="16px">
              {formattedAmount} {earnedAmount?.currency.symbol}
            </ThemedText.DeprecatedBody>
            <ThemedText.DeprecatedBody marginLeft="auto" color="green" fontSize="15px">
              {formatDollarAmount(fiatValue)}
            </ThemedText.DeprecatedBody>
          </RewardAmount>
        </div>

        {/* Destination wallet */}
        <div>
          <SectionLabel>Destination Wallet</SectionLabel>
          <DestinationInfo>
            <WalletDetails>
              <WalletLabel>{claimDestination === 'connected' ? 'Connected Wallet' : 'Another Wallet'}</WalletLabel>
              <WalletAddress>{truncateAddress(destinationAddress)}</WalletAddress>
            </WalletDetails>
          </DestinationInfo>
        </div>

        {/* Confirm Button */}
        <ConfirmButton onClick={handleClaim} disabled={attemptingTxn}>
          {attemptingTxn ? 'Claiming...' : claimButtonText}
        </ConfirmButton>

        {error && <TransactionError error={error} />}
      </StepContainer>
    </ConfirmationTransactionStep>
  )
}
